import { ContractSuretyType, Dtos, SuretyAccountId } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import type { BondRequestCreateFormData } from "@portal/views/agent/request/create/schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isApiError, logError } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { accountExposureQueryKey, accountQueryKey } from "../../account/account.keys";
import { bondQueryKey } from "../../bond/bond.keys";
import { bondRequestPreviewsQueryKey } from "../bond-request.keys";

export const useCreateBondRequest = () => {
	const { post } = useRequest();
	const queryClient = useQueryClient();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const mutation = useMutation({
		mutationKey: ["createBondRequestForAccount"],
		mutationFn: async ({
			accountId,
			data,
		}: {
			accountId: SuretyAccountId;
			data: BondRequestCreateFormData;
		}) => {
			return await post<Dtos.SuretyQuote.Get.Response>(
				`/v2/surety/accounts/${accountId}/quotes`,
				data,
			);
		},
		onSuccess: (_, { accountId, data }) => {
			queryClient.invalidateQueries({ queryKey: accountQueryKey(accountId), exact: true });
			queryClient.invalidateQueries({ queryKey: accountExposureQueryKey(accountId) });
			queryClient.invalidateQueries({ queryKey: bondRequestPreviewsQueryKey() });
			if (data.quote.contractSuretyType === ContractSuretyType.bid_to_final) {
				queryClient.invalidateQueries({
					queryKey: bondQueryKey(data.quote.bidBondId),
					exact: true,
				});
			}
		},
		onError: (error, { data }) => {
			if (
				isApiError(error) &&
				error.key === "final_bond_already_requested" &&
				data.quote.contractSuretyType === ContractSuretyType.bid_to_final
			) {
				queryClient.invalidateQueries({
					queryKey: bondQueryKey(data.quote.bidBondId),
					exact: true,
				});
				triggerErrorModal(error);
			} else {
				logError(error);
				triggerErrorModal(error);
			}
		},
	});

	return {
		createBondRequest: mutation.mutateAsync,
		createBondRequestIsLoading: mutation.isPending,
	};
};
